import type { CSSProperties } from "react";

const GAP = 6;
const PAD = 12;
const MIN_MENU_HEIGHT = 120;

export type ViewportInsets = {
	top: number;
	left: number;
	right: number;
	bottom: number;
};

export function readViewportInsets(): ViewportInsets {
	const vv = window.visualViewport;
	if (!vv) {
		return { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
	}
	return {
		top: vv.offsetTop,
		left: vv.offsetLeft,
		right: vv.offsetLeft + vv.width,
		bottom: vv.offsetTop + vv.height,
	};
}

/** Fixed menu placement for a hub field select summary (modal-safe, flips above footer). */
export function computeHubFieldSelectPopStyle(
	summaryRect: Pick<DOMRect, "top" | "bottom" | "left" | "width">,
	popHeight: number,
	insets: ViewportInsets,
	maxBottomY: number,
	minTopY?: number,
): CSSProperties {
	let width = summaryRect.width;
	let left = summaryRect.left;
	if (left + width > insets.right - PAD) {
		left = insets.right - PAD - width;
	}
	if (left < insets.left + PAD) {
		left = insets.left + PAD;
		width = Math.min(summaryRect.width, insets.right - insets.left - PAD * 2);
	}

	const minTop = Math.max(insets.top + PAD, minTopY ?? insets.top + PAD);
	const maxBottom = Math.min(maxBottomY, insets.bottom) - GAP;
	const belowTop = summaryRect.bottom + GAP;
	const spaceBelow = maxBottom - belowTop;
	const spaceAbove = summaryRect.top - GAP - minTop;

	let top = belowTop;
	let maxHeight = Math.max(MIN_MENU_HEIGHT, spaceBelow);

	// Prefer opening below with a scrollable menu; flip only when below is too tight
	// and there is clearly more room above (avoids jumping the menu off-screen).
	if (spaceBelow < MIN_MENU_HEIGHT && spaceAbove > spaceBelow) {
		const menuHeight = Math.min(Math.max(popHeight, MIN_MENU_HEIGHT), spaceAbove);
		top = Math.max(minTop, summaryRect.top - GAP - menuHeight);
		maxHeight = Math.max(MIN_MENU_HEIGHT, summaryRect.top - GAP - top);
	}

	top = Math.max(minTop, top);
	maxHeight = Math.max(MIN_MENU_HEIGHT, Math.min(maxHeight, maxBottom - top));

	return {
		position: "fixed",
		top,
		left,
		width,
		maxHeight,
		overflowY: "auto",
		zIndex: 210,
		visibility: "visible",
	};
}

export function modalActionsTop(summary: HTMLElement): number {
	const dialog = summary.closest("dialog.modal");
	const actions = dialog?.querySelector(".actions");
	if (actions instanceof HTMLElement) {
		return actions.getBoundingClientRect().top;
	}
	return readViewportInsets().bottom - PAD;
}

export function modalHeaderBottom(summary: HTMLElement): number {
	const dialog = summary.closest("dialog.modal");
	const header = dialog?.querySelector(".row.tight");
	if (header instanceof HTMLElement) {
		return header.getBoundingClientRect().bottom;
	}
	return readViewportInsets().top + PAD;
}
